import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FaCheckCircle, FaShoppingCart, FaCrown, FaRedo, FaCalendarCheck } from 'react-icons/fa';
import { Elements, PaymentElement } from '@stripe/react-stripe-js';
import { Stripe, StripeElementsOptions, loadStripe } from '@stripe/stripe-js';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import api from '../api/axiosInstance';

// Stripe promise for one-time payment Elements
const stripePromiseOneTime = loadStripe('pk_live_51PexFn2LxS9rV2S4jEp30qRFF8DK5Crr6KxDD3NsnEgLrl12PZQ7hCENsCKyLf7O6TIuSMfNbnjfVHeGk0Osh2EW00Rf4hdjgi');

// Separate component for one-time payment form (uses its own Elements context with PaymentIntent)
interface OneTimePaymentFormProps {
    clientSecret: string;
    returnUrl: string;
    productId: number;
}

const OneTimePaymentForm: React.FC<OneTimePaymentFormProps> = ({ clientSecret, returnUrl, productId }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isProcessing, setIsProcessing] = useState(false);

    const handleOneTimeSubmit = async () => {
        if (!stripe || !elements) return;

        setIsProcessing(true);

        const { error } = await stripe.confirmPayment({
            elements,
            confirmParams: {
                return_url: returnUrl,
            },
        });

        if (error) {
            console.error('Payment failed:', error.message);
        }
        setIsProcessing(false);
    };

    return (
        <>
            <PaymentElement />
            <Button 
                onClick={handleOneTimeSubmit} 
                disabled={!stripe || isProcessing}
                style={{ marginTop: '20px', width: '100%' }}
            >
                {isProcessing ? 'Spracováva sa...' : 'Dokončiť platbu'}
            </Button>
        </>
    );
};

interface SubscriptionProduct {
    id: number;
    name: string;
    price: number;
    icon: string;
    durationDays: number;
    isRecurring: boolean;
}

const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
`;

const Modal = styled.div`
    background: white;
    width: 700px;
    max-width: 100%;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    background-color: #1C1C1E;
    overflow-y: auto;
`;

const ModalTitle = styled.h2`
    font-size: 1.5em;
    margin: 0;
    display: flex;
    align-items: center;
    color: #f56607;
`;

const PriceText = styled.div`
    font-size: 0.9em;
    color: white;
`;

const StepNav = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: flex-start;
    }
`;

const StepItem = styled.div<{ active: boolean }>`
    display: flex;
    align-items: center;
    font-weight: ${(props) => (props.active ? 'bold' : 'normal')};
    color: ${(props) => (props.active ? '#FF7F00' : '#ccc')};
    position: relative;
`;

const CheckIcon = styled(FaCheckCircle)`
    color: #FF7F00;
    margin-right: 8px;
`;

const ModalContent = styled.div`
    margin-bottom: 20px;
    color: white;
    overflow-y: auto;
    max-height: 60vh;
`;

const ModalFooter = styled.div`
    display: flex;
    justify-content: space-between;
`;

const Button = styled.button`
    background-color: #FF7F00;
    color: white;
    padding: 10px 20px;
    border: none;
    border-radius: 5px;
    cursor: pointer;

    &:hover {
        background-color: #e1860f;
    }

    &:disabled {
        background-color: #666;
        cursor: not-allowed;
    }
`;

const ModalStep = styled.div`
    border-top: 1px solid #ddd;
    padding: 10px 0;
`;

const StepTitle = styled.h3`
    font-size: 1.2em;
    margin: 10px 0;
    color: #f56607;
`;

const ProductImage = styled.img`
    width: 100px;
    height: 100px;
    border-radius: 8px;
    background-color: #e0e0e0;
`;

const Divider = styled.div`
    width: 100%;
    height: 1px;
    background-color: #ccc;
    margin: 20px 0;
`;

const Input = styled.input`
    margin: 10px 0;
    padding: 10px;
    width: 100%;
    box-sizing: border-box;
    border-radius: 5px;
    border: 1px solid #f56607;
    background-color: #333;
    color: white;
`;

const ErrorMessage = styled.label`
    margin-top: 20px;
    font-weight: bold;
    display: block;
    text-align: center;
    color: red;
`;

interface ModalComponentProps {
    isOpen: boolean;
    onClose: () => void;
    stripePromise: Promise<Stripe | null>;
    paymentOptions: StripeElementsOptions;
}

const CenteredContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    margin-top: 20px;
`;

const CenteredText = styled.p`
    font-size: 18px;
    font-weight: bold;
    color: #f56607;
    margin: 10px 0;
`;

const CenteredPrice = styled.p`
    font-size: 20px;
    font-weight: bold;
    color: #ffffff;
    margin-top: 5px;
`;

// Package selection styles
const PackagesSection = styled.div`
    margin: 20px 0;
`;

const SectionTitle = styled.h4`
    color: #f56607;
    font-size: 1em;
    margin: 20px 0 10px 0;
    padding-bottom: 8px;
    border-bottom: 1px solid #3C3C3E;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const SectionSubtitle = styled.p`
    color: #888;
    font-size: 0.8em;
    margin: 0 0 15px 0;
`;

const PackagesContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    margin: 10px 0;

    @media (max-width: 600px) {
        grid-template-columns: 1fr;
    }
`;

const SubscriptionContainer = styled.div`
    margin: 10px 0;
`;

const PackageCard = styled.div<{ selected: boolean; isBestValue?: boolean; isSubscription?: boolean }>`
    background: #2C2C2E;
    border: ${(props) => (props.selected ? '3px solid #FF7F00' : '2px solid #3C3C3E')};
    border-radius: 12px;
    padding: 16px;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;

    &:hover {
        border-color: #FF7F00;
        transform: translateY(-2px);
    }
    
`;

const PackageName = styled.h4`
    color: white;
    margin: 0 0 8px 0;
    font-size: 1em;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const PackagePrice = styled.div`
    color: #f56607;
    font-size: 1.4em;
    font-weight: bold;
    margin-bottom: 4px;
`;

const PackageDetails = styled.div`
    color: #aaa;
    font-size: 0.8em;
`;

const BillingType = styled.span<{ isRecurring: boolean }>`
    display: inline-block;
    margin-top: 8px;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 0.75em;
    font-weight: bold;
    background: ${(props) => (props.isRecurring ? '#5856D6' : '#2d4a2d')};
    color: ${(props) => (props.isRecurring ? '#fff' : '#4CAF50')};
`;

const SubscriptionBadge = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 8px;
    padding: 6px 10px;
    background: rgba(88, 86, 214, 0.2);
    border-radius: 6px;
    font-size: 0.75em;
    color: #A5A4F3;
`;

const OneTimeBadge = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 8px;
    padding: 6px 10px;
    background: rgba(76, 175, 80, 0.2);
    border-radius: 6px;
    font-size: 0.75em;
    color: #81C784;
`;

const LoadingSpinner = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 40px;
    color: #f56607;
`;

const InfoBox = styled.div`
    background: rgba(255, 127, 0, 0.1);
    border: 1px solid rgba(255, 127, 0, 0.3);
    border-radius: 8px;
    padding: 12px;
    margin-top: 15px;
    font-size: 0.85em;
    color: #ccc;
    
    strong {
        color: #f56607;
    }
`;

const BuyPremium: React.FC<ModalComponentProps> = ({ isOpen, onClose, stripePromise, paymentOptions }) => {
    const user = useSelector((state: RootState) => state.auth.user);
    const theFullName = user ? user.fullName : '';
    const userId = user ? user.id : null;
    
    // Package selection state
    const [packages, setPackages] = useState<SubscriptionProduct[]>([]);
    const [selectedPackage, setSelectedPackage] = useState<SubscriptionProduct | null>(null);
    const [loadingPackages, setLoadingPackages] = useState(true);

    const [fullName, setFullName] = useState(theFullName);
    const [currentStep, setCurrentStep] = useState(0);
    const [streetAddress, setStreetAddress] = useState('');
    const [postcode, setPostcode] = useState('');
    const [city, setCity] = useState('');
    const [country, setCountry] = useState('');

    const [billingDetails, setBillingDetails] = useState({
        fullName: '',
        streetAddress: '',
        postcode: '',
        city: '',
        country: '',
    });

    const [showMissingFieldsMessage, setShowMissingFieldsMessage] = useState(false);
    
    // For one-time payments: store the PaymentIntent clientSecret
    const [oneTimeClientSecret, setOneTimeClientSecret] = useState<string | null>(null);
    const [loadingPaymentIntent, setLoadingPaymentIntent] = useState(false);

    const stripe = useStripe();
    const elements = useElements();

    const [missingFields, setMissingFields] = useState({
        fullName: false,
        streetAddress: false,
        postcode: false,
        city: false,
        country: false,
    });

    // Fetch available subscription packages
    useEffect(() => {
        const fetchPackages = async () => {
            try {
                setLoadingPackages(true);
                const response = await api.get('/products/subscriptions');
                const products = response.data as SubscriptionProduct[];
                setPackages(products);
                // Default to the 30-day package (or first package if not found)
                const defaultPackage = products.find(p => p.durationDays === 30) || products[0];
                if (defaultPackage) {
                    setSelectedPackage(defaultPackage);
                }
            } catch (error) {
                console.error('Error fetching subscription packages:', error);
            } finally {
                setLoadingPackages(false);
            }
        };
        if (isOpen) {
            fetchPackages();
        }
    }, [isOpen]);

    useEffect(() => {
        if (userId) {
            const fetchBillingDetails = async () => {
                try {
                    const response = await api.get(`/account/${userId}`);
                    const { fullName, streetAddress, zipcode, city, country } = response.data;
                    setFullName(fullName || '');
                    setStreetAddress(streetAddress || '');
                    setPostcode(zipcode || '');
                    setCity(city || '');
                    setCountry(country || '');
                } catch (error) {
                    console.error('Error fetching billing details:', error);
                }
            };
            fetchBillingDetails();
        }
    }, [userId]);

    const handleNextStep = async () => {
        if (validateStep()) {
            // Save billing details when moving from step 1
            if (currentStep === 1) {
                try {
                    await api.put(`/account/${userId}`, {
                        fullname: fullName,
                        streetAddress: streetAddress,
                        zipcode: postcode,
                        city: city,
                        country: country,
                    });
                } catch (error) {}
            }
            
            // When moving to payment step (step 3) for one-time payments, create PaymentIntent first
            if (currentStep === 2 && selectedPackage && !selectedPackage.isRecurring) {
                setLoadingPaymentIntent(true);
                try {
                    const response = await api.post(`/payment-intent/${userId}?productId=${selectedPackage.id}`);
                    const { clientSecret } = response.data;
                    setOneTimeClientSecret(clientSecret);
                } catch (error: any) {
                    console.error('Error creating PaymentIntent:', error);
                } finally {
                    setLoadingPaymentIntent(false);
                }
            }
            
            setCurrentStep(currentStep + 1);
        }
    };

    const handlePreviousStep = () => {
        setCurrentStep((prevStep) => prevStep - 1);
    };

    const validateStep = () => {
        if (currentStep === 0) {
            // Validate package selection
            return selectedPackage !== null;
        }
        if (currentStep === 1) {
            const newMissingFields = {
                fullName: fullName.trim() === '',
                streetAddress: streetAddress.trim() === '',
                postcode: postcode.trim() === '',
                city: city.trim() === '',
                country: country.trim() === '',
            };

            setMissingFields(newMissingFields);
            const hasMissingFields = Object.values(newMissingFields).includes(true);
            setShowMissingFieldsMessage(hasMissingFields);
            return !hasMissingFields;
        }
        return true;
    };

    const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        if (!stripe || !elements || !selectedPackage) return;

        // Pass productId via URL parameter (more reliable than sessionStorage)
        const returnUrl = `https://goly-esa.sk/payment-status?productId=${selectedPackage.id}`;

        // For recurring subscriptions, use SetupIntent flow
        const { error } = await stripe.confirmSetup({
            elements,
            confirmParams: {
                return_url: returnUrl,
            },
        });

        if (error) {
            console.error('Payment failed:', error.message);
        }
    };

    const formatDuration = (days: number): string => {
        if (days === 1) return '1 Deň';
        if (days === 30) return '30 Dní';
        if (days === 90) return '3 Mesiace';
        if (days === 365) return '1 Rok';
        return `${days} dní`;
    };

    const getBillingPeriod = (pkg: SubscriptionProduct): string => {
        if (!pkg.isRecurring) return 'Jednorazová platba';
        if (pkg.durationDays === 30) return 'Mesačne';
        if (pkg.durationDays === 90) return 'Štvrťročne';
        if (pkg.durationDays === 365) return 'Ročne';
        return 'Opakovane';
    };

    if (!isOpen) return null;

    const totalPrice = selectedPackage?.price || 0;

    return (
        <Overlay onClick={onClose}>
            <Modal onClick={(e) => e.stopPropagation()}>
                <ModalTitle>
                    <FaShoppingCart />
                    Košík
                </ModalTitle>
                <PriceText>{totalPrice.toFixed(2)} EUR</PriceText>
                <StepNav>
                    {['Balíček', 'Fakturačné Údaje', 'Rekapitulácia', 'Platba'].map((step, index) => (
                        <StepItem key={index} active={currentStep === index}>
                            {currentStep >= index ? <CheckIcon /> : null}
                            {step}
                        </StepItem>
                    ))}
                </StepNav>
                <ModalContent>
                    {currentStep === 0 && (
                        <ModalStep>
                            <StepTitle>Vyberte si balíček</StepTitle>
                            {loadingPackages ? (
                                <LoadingSpinner>Načítavam balíčky...</LoadingSpinner>
                            ) : (
                                <PackagesSection>
                                    {/* One-time payment packages */}
                                    <SectionTitle>
                                        <FaCalendarCheck color="#4CAF50" />
                                        Jednorazové balíčky
                                    </SectionTitle>
                                    <SectionSubtitle>
                                        Zaplaťte raz a užívajte si prístup po vybranú dobu. Bez automatického obnovovania.
                                    </SectionSubtitle>
                                    <PackagesContainer>
                                        {packages
                                            .filter((pkg) => !pkg.isRecurring)
                                            .map((pkg) => (
                                                <PackageCard
                                                    key={pkg.id}
                                                    selected={selectedPackage?.id === pkg.id}
                                                    isBestValue={pkg.durationDays === 365}
                                                    onClick={() => setSelectedPackage(pkg)}
                                                >
                                                    <PackageName>
                                                        <FaCrown color="#FFD700" />
                                                        {pkg.name}
                                                    </PackageName>
                                                    <PackagePrice>{pkg.price.toFixed(2)} €</PackagePrice>
                                                    <PackageDetails>
                                                        {formatDuration(pkg.durationDays)} prístupu
                                                    </PackageDetails>
                                                    <OneTimeBadge>
                                                        <FaCalendarCheck />
                                                        Jednorazová platba
                                                    </OneTimeBadge>
                                                </PackageCard>
                                            ))}
                                    </PackagesContainer>

                                    {/* Subscription packages */}
                                    <SectionTitle>
                                        <FaRedo color="#5856D6" />
                                        Predplatné
                                    </SectionTitle>
                                    <SectionSubtitle>
                                        Automaticky sa obnovuje každý mesiac. Môžete kedykoľvek zrušiť.
                                    </SectionSubtitle>
                                    <SubscriptionContainer>
                                        {packages
                                            .filter((pkg) => pkg.isRecurring)
                                            .map((pkg) => (
                                                <PackageCard
                                                    key={pkg.id}
                                                    selected={selectedPackage?.id === pkg.id}
                                                    isSubscription={true}
                                                    onClick={() => setSelectedPackage(pkg)}
                                                >
                                                    <PackageName>
                                                        <FaCrown color="#FFD700" />
                                                        {pkg.name}
                                                    </PackageName>
                                                    <PackagePrice>{pkg.price.toFixed(2)} € / mesiac</PackagePrice>
                                                    <PackageDetails>
                                                        Neobmedzený prístup
                                                    </PackageDetails>
                                                    <SubscriptionBadge>
                                                        <FaRedo />
                                                        Automatické obnovovanie mesačne
                                                    </SubscriptionBadge>
                                                </PackageCard>
                                            ))}
                                    </SubscriptionContainer>

                                    <InfoBox>
                                        <strong>💡 Tip:</strong> Pri jednorazových balíčkoch platíte len raz. 
                                        Predplatné sa automaticky obnovuje, ale môžete ho kedykoľvek zrušiť v nastaveniach účtu.
                                    </InfoBox>
                                </PackagesSection>
                            )}
                            <Divider />
                        </ModalStep>
                    )}
                    {currentStep === 1 && (
                        <ModalStep>
                            <StepTitle>Fakturačné Údaje</StepTitle>
                            <Input
                                type="text"
                                placeholder="Celé Meno"
                                value={fullName}
                                onChange={(e) => setFullName(e.target.value)}
                                className={missingFields.fullName ? 'input-error' : ''}
                            />
                            <Input
                                type="text"
                                placeholder="Adresa"
                                value={streetAddress}
                                onChange={(e) => setStreetAddress(e.target.value)}
                                className={missingFields.streetAddress ? 'input-error' : ''}
                            />
                            <Input
                                type="text"
                                placeholder="PSČ"
                                value={postcode}
                                onChange={(e) => setPostcode(e.target.value)}
                                className={missingFields.postcode ? 'input-error' : ''}
                            />
                            <Input
                                type="text"
                                placeholder="Mesto"
                                value={city}
                                onChange={(e) => setCity(e.target.value)}
                                className={missingFields.city ? 'input-error' : ''}
                            />
                            <Input
                                type="text"
                                placeholder="Krajina"
                                value={country}
                                onChange={(e) => setCountry(e.target.value)}
                                className={missingFields.country ? 'input-error' : ''}
                            />
                            {showMissingFieldsMessage && <ErrorMessage>Vyplňte všetky položky</ErrorMessage>}
                        </ModalStep>
                    )}
                    {currentStep === 2 && (
                        <ModalStep>
                            <CenteredContent>
                                <StepTitle>Rekapitulácia</StepTitle>
                                <ProductImage src="/g.png" alt="Góly a Esá" />
                                <CenteredText>{selectedPackage?.name || 'Premium Balíček'}</CenteredText>
                                <CenteredPrice>
                                    Celková cena: {totalPrice.toFixed(2)} EUR
                                </CenteredPrice>
                                {selectedPackage && (
                                    selectedPackage.isRecurring ? (
                                        <SubscriptionBadge style={{ marginTop: '15px', justifyContent: 'center' }}>
                                            <FaRedo />
                                            Predplatné - automaticky sa obnovuje každý mesiac
                                        </SubscriptionBadge>
                                    ) : (
                                        <OneTimeBadge style={{ marginTop: '15px', justifyContent: 'center' }}>
                                            <FaCalendarCheck />
                                            Jednorazová platba - prístup na {formatDuration(selectedPackage.durationDays)}
                                        </OneTimeBadge>
                                    )
                                )}
                            </CenteredContent>
                            <Divider />
                        </ModalStep>
                    )}
                    {currentStep === 3 && (
                        <ModalStep>
                            <StepTitle>Platba</StepTitle>
                            {selectedPackage && !selectedPackage.isRecurring && oneTimeClientSecret ? (
                                // For one-time payments: use new Elements with PaymentIntent (shows correct amount in 3D Secure)
                                <Elements 
                                    stripe={stripePromiseOneTime} 
                                    options={{
                                        clientSecret: oneTimeClientSecret,
                                        appearance: {
                                            theme: 'night',
                                            variables: {
                                                fontFamily: 'Roboto, sans-serif',
                                                fontSizeBase: '16px',
                                                colorPrimary: '#ffffff',
                                                colorBackground: '#000',
                                                colorText: '#f56607',
                                                colorTextPlaceholder: '#888',
                                                colorTextSecondary: '#FFA500',
                                                colorDanger: '#FF4D4D',
                                                borderRadius: '8px',
                                            },
                                            rules: {
                                                '.Input': {
                                                    color: '#FFFFFF',
                                                },
                                            },
                                        },
                                    }}
                                >
                                    <OneTimePaymentForm 
                                        clientSecret={oneTimeClientSecret}
                                        returnUrl={`https://goly-esa.sk/payment-status?productId=${selectedPackage.id}`}
                                        productId={selectedPackage.id}
                                    />
                                </Elements>
                            ) : loadingPaymentIntent ? (
                                <LoadingSpinner>Načítavam platobný formulár...</LoadingSpinner>
                            ) : (
                                // For recurring subscriptions: use existing Elements with SetupIntent
                                <PaymentElement />
                            )}
                        </ModalStep>
                    )}
                </ModalContent>
                <ModalFooter>
                    {currentStep > 0 && <Button onClick={handlePreviousStep}>Späť</Button>}
                    {currentStep < 3 ? (
                        <Button onClick={handleNextStep} disabled={(currentStep === 0 && !selectedPackage) || loadingPaymentIntent}>
                            {loadingPaymentIntent ? 'Načítavam...' : 'Ďalej'}
                        </Button>
                    ) : (
                        // Only show Dokončiť button for recurring subscriptions (one-time has its own button in the form)
                        selectedPackage?.isRecurring && <Button onClick={handleSubmit}>Dokončiť</Button>
                    )}
                </ModalFooter>
            </Modal>
        </Overlay>
    );
};

export default BuyPremium;
